
import { useEffect, useRef, useState, useMemo } from 'react';
import { COUNTRIES, getCountryCoordinates } from '../../data/countries';

interface WorldMapTrackerProps {
  origin: string;
  destination: string;
  stopovers?: string[];
  transportMode?: string;
  currentLocation?: string;
  isStopped?: boolean;
  stopReason?: string;
  carrier?: string;
}

interface RoutePoint {
  lat: number;
  lng: number;
  label: string;
  type: 'origin' | 'stopover' | 'destination';
}

function mercatorProject(
  lat: number, lng: number,
  width: number, height: number,
  padding: number = 20
): [number, number] {
  const usableW = width - padding * 2;
  const usableH = height - padding * 2;
  const x = padding + ((lng + 180) / 360) * usableW;
  const latRad = (lat * Math.PI) / 180;
  const mercN = Math.log(Math.tan(Math.PI / 4 + latRad / 2));
  const clampedMerc = Math.max(-Math.PI, Math.min(Math.PI, mercN));
  const y = padding + usableH / 2 - (clampedMerc / Math.PI) * (usableH / 2);
  return [x, y];
}

function interpolateGreatCircle(
  lat1: number, lng1: number,
  lat2: number, lng2: number,
  t: number
): [number, number] {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const toDeg = (r: number) => (r * 180) / Math.PI;
  const phi1 = toRad(lat1);
  const phi2 = toRad(lat2);
  const lam1 = toRad(lng1);
  const lam2 = toRad(lng2);
  const dPhi = phi2 - phi1;
  const dLam = lam2 - lam1;
  const a = Math.sin(dPhi / 2) ** 2 + Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLam / 2) ** 2;
  const delta = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  if (delta < 1e-10) return [lat1, lng1];
  const A = Math.sin((1 - t) * delta) / Math.sin(delta);
  const B = Math.sin(t * delta) / Math.sin(delta);
  const x = A * Math.cos(phi1) * Math.cos(lam1) + B * Math.cos(phi2) * Math.cos(lam2);
  const y = A * Math.cos(phi1) * Math.sin(lam1) + B * Math.cos(phi2) * Math.sin(lam2);
  const z = A * Math.sin(phi1) + B * Math.sin(phi2);
  return [toDeg(Math.atan2(z, Math.sqrt(x * x + y * y))), toDeg(Math.atan2(y, x))];
}

export default function WorldMapTracker({
  origin,
  destination,
  stopovers = [],
  transportMode = 'air',
  isStopped = false,
  stopReason,
  carrier,
}: WorldMapTrackerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 900, height: 450 });
  const [progress, setProgress] = useState(0);
  const animRef = useRef<number>(0);
  const progressRef = useRef(0);

  // Responsive dimensions
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const w = containerRef.current.offsetWidth;
        setDimensions({ width: w, height: Math.max(280, Math.min(450, w * 0.5)) });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Build route points
  const routePoints = useMemo<RoutePoint[]>(() => {
    const originCoords = getCountryCoordinates(origin);
    const destCoords = getCountryCoordinates(destination);
    if (!originCoords || !destCoords) return [];
    const pts: RoutePoint[] = [
      { lat: originCoords[0], lng: originCoords[1], label: origin, type: 'origin' },
    ];
    if (stopovers && stopovers.length > 0) {
      stopovers.forEach((s) => {
        const coords = getCountryCoordinates(s);
        if (coords) {
          pts.push({ lat: coords[0], lng: coords[1], label: s, type: 'stopover' });
        }
      });
    }
    pts.push({ lat: destCoords[0], lng: destCoords[1], label: destination, type: 'destination' });
    return pts;
  }, [origin, destination, stopovers]);

  // Countries in route
  const routeCountryNames = useMemo(() => {
    const names = new Set<string>();
    names.add(origin);
    names.add(destination);
    if (stopovers) stopovers.forEach((s) => names.add(s));
    return names;
  }, [origin, destination, stopovers]);

  // Generate arc points between two coordinates
  const generateArcPoints = (
    lat1: number, lng1: number,
    lat2: number, lng2: number,
    numPoints: number = 60
  ): [number, number][] => {
    const pts: [number, number][] = [];
    for (let i = 0; i <= numPoints; i++) {
      pts.push(interpolateGreatCircle(lat1, lng1, lat2, lng2, i / numPoints));
    }
    return pts;
  };

  // Build all arcs
  const allArcs = useMemo(() => {
    if (routePoints.length < 2) return [];
    const arcs: [number, number][][] = [];
    for (let i = 0; i < routePoints.length - 1; i++) {
      const from = routePoints[i];
      const to = routePoints[i + 1];
      arcs.push(generateArcPoints(from.lat, from.lng, to.lat, to.lng, 60));
    }
    return arcs;
  }, [routePoints]);

  // Animation
  useEffect(() => {
    if (isStopped || routePoints.length < 2) {
      progressRef.current = isStopped ? 0.5 : 0;
      setProgress(progressRef.current);
      return;
    }
    const animate = () => {
      progressRef.current += 0.003;
      if (progressRef.current > 1) progressRef.current = 0;
      setProgress(progressRef.current);
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [isStopped, routePoints]);

  // Get vehicle position
  const getVehiclePosition = (): [number, number] => {
    if (allArcs.length === 0 || routePoints.length < 2) return [0, 0];
    const totalPoints = allArcs.reduce((sum, arc) => sum + arc.length, 0);
    const targetIdx = Math.floor(progress * totalPoints);
    let count = 0;
    for (let a = 0; a < allArcs.length; a++) {
      for (let i = 0; i < allArcs[a].length; i++) {
        if (count >= targetIdx) {
          return mercatorProject(allArcs[a][i][0], allArcs[a][i][1], dimensions.width, dimensions.height);
        }
        count++;
      }
    }
    const last = routePoints[routePoints.length - 1];
    return mercatorProject(last.lat, last.lng, dimensions.width, dimensions.height);
  };

  // Build SVG arc path
  const buildArcPath = (arc: [number, number][]): string => {
    const W = dimensions.width;
    const H = dimensions.height;
    let d = '';
    arc.forEach((pt, idx) => {
      const [x, y] = mercatorProject(pt[0], pt[1], W, H);
      if (idx === 0) d += `M${x},${y}`;
      else d += ` L${x},${y}`;
    });
    return d;
  };

  // Build animated path (up to current progress)
  const buildAnimatedPath = (): string => {
    if (allArcs.length === 0) return '';
    const W = dimensions.width;
    const H = dimensions.height;
    const totalPoints = allArcs.reduce((sum, arc) => sum + arc.length, 0);
    const drawnPoints = Math.floor(progress * totalPoints);
    let d = '';
    let count = 0;
    let started = false;
    for (let a = 0; a < allArcs.length; a++) {
      for (let i = 0; i < allArcs[a].length; i++) {
        if (count > drawnPoints) break;
        const [x, y] = mercatorProject(allArcs[a][i][0], allArcs[a][i][1], W, H);
        if (!started) { d += `M${x},${y}`; started = true; }
        else d += ` L${x},${y}`;
        count++;
      }
      if (count > drawnPoints) break;
    }
    return d;
  };

  if (routePoints.length < 2) {
    return (
      <div className="bg-gray-50 rounded-xl p-8 text-center">
        <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
          <i className="ri-map-pin-line text-4xl text-gray-300"></i>
        </div>
        <p className="text-gray-600 text-sm">Impossible d&apos;afficher la carte pour ce trajet</p>
      </div>
    );
  }

  const W = dimensions.width;
  const H = dimensions.height;
  const vehiclePos = getVehiclePosition();
  const isMobile = W < 500;
  const PAD = isMobile ? 12 : 20;

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${isStopped ? 'bg-red-100' : 'bg-[#0066CC]/10'}`}>
            <i className={`${isStopped ? 'ri-alert-line text-red-600' : transportMode === 'sea' ? 'ri-ship-line text-[#0066CC]' : 'ri-plane-line text-[#0066CC]'} text-lg sm:text-xl`}></i>
          </div>
          <div className="min-w-0">
            <h4 className="font-semibold text-gray-900 text-sm sm:text-base truncate">
              {isStopped ? 'Envoi arr\u00eat\u00e9' : 'Carte mondiale du trajet'}
            </h4>
            <p className="text-xs sm:text-sm text-gray-500 truncate">
              {isStopped
                ? 'Transport interrompu'
                : `Par ${transportMode === 'sea' ? 'bateau' : 'avion'}${stopovers && stopovers.length > 0 ? ` \u2014 ${stopovers.length} escale${stopovers.length > 1 ? 's' : ''}` : ''}${carrier ? ` \u2022 ${carrier}` : ''}`
              }
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {carrier && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-lg">
              <i className="ri-truck-line text-gray-600 text-xs"></i>
              <span className="text-xs font-semibold text-gray-700">{carrier}</span>
            </div>
          )}
          {isStopped && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg animate-pulse">
              <i className="ri-error-warning-fill text-red-600 text-sm"></i>
              <span className="text-xs font-bold text-red-600">ARR\u00caT\u00c9</span>
            </div>
          )}
        </div>
      </div>

      {/* Stop reason */}
      {isStopped && stopReason && (
        <div className="p-3 sm:p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg animate-pulse">
          <div className="flex items-start gap-2 sm:gap-3">
            <i className="ri-error-warning-fill text-red-600 text-base sm:text-lg flex-shrink-0 mt-0.5"></i>
            <div className="min-w-0">
              <div className="text-xs sm:text-sm font-bold text-red-900 mb-0.5">Probl\u00e8me signal\u00e9</div>
              <p className="text-xs sm:text-sm text-red-800 break-words">{stopReason}</p>
            </div>
          </div>
        </div>
      )}

      {/* Map Container */}
      <div
        ref={containerRef}
        className={`w-full rounded-xl overflow-hidden shadow-lg relative ${isStopped ? 'ring-2 ring-red-400 ring-offset-2' : ''}`}
        style={{ height: `${H}px` }}
      >
        {/* Real world map background image */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/5e851602006d172f18581739a865f291.jpg"
            alt="World Map"
            className="w-full h-full object-cover object-center"
          />
          {/* Overlay for better contrast */}
          <div className={`absolute inset-0 ${isStopped ? 'bg-red-50/60' : transportMode === 'sea' ? 'bg-sky-50/50' : 'bg-slate-50/50'}`}></div>
        </div>

        {/* SVG overlay for dots, routes, and vehicle */}
        <svg
          width={W}
          height={H}
          viewBox={`0 0 ${W} ${H}`}
          className="absolute inset-0 w-full h-full"
          style={{ zIndex: 2 }}
        >
          <defs>
            <filter id="glow-blue" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="glow-red" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <radialGradient id="vehicle-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={isStopped ? '#DC2626' : '#0066CC'} stopOpacity="0.4" />
              <stop offset="100%" stopColor={isStopped ? '#DC2626' : '#0066CC'} stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* All country dots */}
          {COUNTRIES.map((country) => {
            const [cx, cy] = mercatorProject(country.coordinates[0], country.coordinates[1], W, H, PAD);
            const isInRoute = routeCountryNames.has(country.name);
            if (isInRoute) return null;
            return (
              <circle
                key={country.code}
                cx={cx}
                cy={cy}
                r={isMobile ? 1 : 1.5}
                fill="rgba(100,116,139,0.3)"
              />
            );
          })}

          {/* Dashed route path (full) */}
          {allArcs.map((arc, idx) => (
            <path
              key={`dash-${idx}`}
              d={buildArcPath(arc)}
              fill="none"
              stroke={isStopped ? 'rgba(220,38,38,0.2)' : 'rgba(0,102,204,0.2)'}
              strokeWidth={isMobile ? 1.5 : 2}
              strokeDasharray="8,5"
            />
          ))}

          {/* Animated solid path */}
          <path
            d={buildAnimatedPath()}
            fill="none"
            stroke={isStopped ? '#DC2626' : '#0066CC'}
            strokeWidth={isMobile ? 2 : 3}
            strokeLinecap="round"
            strokeLinejoin="round"
            filter={isStopped ? 'url(#glow-red)' : 'url(#glow-blue)'}
          />

          {/* Route waypoints */}
          {routePoints.map((point, idx) => {
            const [px, py] = mercatorProject(point.lat, point.lng, W, H, PAD);
            const color = point.type === 'origin' ? '#0066CC' : point.type === 'destination' ? '#00CC88' : '#F59E0B';
            const outerR = isMobile ? 10 : 14;
            const innerR = isMobile ? 5 : 7;
            const dotR = isMobile ? 2 : 2.5;
            return (
              <g key={`wp-${idx}`}>
                {/* Outer glow */}
                <circle cx={px} cy={py} r={outerR} fill={color} opacity={0.12} />
                {/* Main circle */}
                <circle cx={px} cy={py} r={innerR} fill={color} stroke="#fff" strokeWidth={isMobile ? 1.5 : 2.5} />
                {/* Inner dot */}
                <circle cx={px} cy={py} r={dotR} fill="#fff" />
                {/* Label */}
                <g>
                  <rect
                    x={px - (point.label.length * (isMobile ? 2.8 : 3.2)) - 4}
                    y={py - (isMobile ? 22 : 28)}
                    width={point.label.length * (isMobile ? 5.6 : 6.4) + 8}
                    height={isMobile ? 14 : 18}
                    rx={3}
                    fill="rgba(255,255,255,0.92)"
                    stroke="rgba(0,0,0,0.08)"
                    strokeWidth={0.5}
                  />
                  <text
                    x={px}
                    y={py - (isMobile ? 13 : 17)}
                    textAnchor="middle"
                    fill="#1f2937"
                    fontSize={isMobile ? 8 : 10}
                    fontWeight={600}
                    fontFamily="Inter, system-ui, sans-serif"
                  >
                    {point.label}
                  </text>
                </g>
              </g>
            );
          })}

          {/* Vehicle */}
          <g>
            {/* Vehicle glow */}
            <circle cx={vehiclePos[0]} cy={vehiclePos[1]} r={isMobile ? 16 : 22} fill="url(#vehicle-glow)">
              <animate attributeName="r" values={isMobile ? '14;20;14' : '18;26;18'} dur="2s" repeatCount="indefinite" />
            </circle>
            {/* Pulse ring */}
            {!isStopped && (
              <circle cx={vehiclePos[0]} cy={vehiclePos[1]} r={isMobile ? 10 : 14} fill="none" stroke="rgba(0,102,204,0.25)" strokeWidth={1.5}>
                <animate attributeName="r" values={isMobile ? '10;18;10' : '14;24;14'} dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.4;0;0.4" dur="2s" repeatCount="indefinite" />
              </circle>
            )}
            {isStopped && (
              <circle cx={vehiclePos[0]} cy={vehiclePos[1]} r={isMobile ? 12 : 16} fill="none" stroke="rgba(220,38,38,0.4)" strokeWidth={2}>
                <animate attributeName="r" values={isMobile ? '12;20;12' : '16;26;16'} dur="1.2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.6;0;0.6" dur="1.2s" repeatCount="indefinite" />
              </circle>
            )}
            {/* Vehicle circle */}
            <circle
              cx={vehiclePos[0]}
              cy={vehiclePos[1]}
              r={isMobile ? 10 : 13}
              fill={isStopped ? '#DC2626' : transportMode === 'sea' ? '#0077B6' : '#0066CC'}
              stroke="#fff"
              strokeWidth={2}
            />
            {/* Vehicle icon text */}
            <text
              x={vehiclePos[0]}
              y={vehiclePos[1] + 1}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={isMobile ? 11 : 13}
              fill="#fff"
            >
              {isStopped ? '\u26A0' : transportMode === 'sea' ? '\u{1F6A2}' : '\u2708'}
            </text>
            {/* Stopped badge */}
            {isStopped && (
              <g>
                <rect
                  x={vehiclePos[0] - (isMobile ? 22 : 28)}
                  y={vehiclePos[1] + (isMobile ? 14 : 18)}
                  width={isMobile ? 44 : 56}
                  height={isMobile ? 14 : 18}
                  rx={isMobile ? 3 : 5}
                  fill="#DC2626"
                >
                  <animate attributeName="opacity" values="0.85;1;0.85" dur="1s" repeatCount="indefinite" />
                </rect>
                <text
                  x={vehiclePos[0]}
                  y={vehiclePos[1] + (isMobile ? 23 : 29)}
                  textAnchor="middle"
                  fill="#fff"
                  fontSize={isMobile ? 7 : 9}
                  fontWeight={700}
                  fontFamily="Inter, system-ui, sans-serif"
                >
                  ARR\u00caT\u00c9
                </text>
              </g>
            )}
          </g>

          {/* Legend */}
          <g transform={`translate(${isMobile ? 8 : 14}, ${H - (isMobile ? 10 : 14)})`}>
            <circle cx={0} cy={0} r={isMobile ? 3 : 4} fill="#0066CC" />
            <text x={isMobile ? 6 : 8} y={isMobile ? 3 : 3.5} fontSize={isMobile ? 7 : 9} fill="#6b7280" fontWeight={500} fontFamily="Inter, system-ui, sans-serif">Origine</text>

            {stopovers && stopovers.length > 0 && (
              <>
                <circle cx={isMobile ? 42 : 64} cy={0} r={isMobile ? 3 : 4} fill="#F59E0B" />
                <text x={isMobile ? 48 : 72} y={isMobile ? 3 : 3.5} fontSize={isMobile ? 7 : 9} fill="#6b7280" fontWeight={500} fontFamily="Inter, system-ui, sans-serif">Escale</text>
              </>
            )}

            <circle cx={stopovers && stopovers.length > 0 ? (isMobile ? 82 : 120) : (isMobile ? 42 : 64)} cy={0} r={isMobile ? 3 : 4} fill="#00CC88" />
            <text x={stopovers && stopovers.length > 0 ? (isMobile ? 88 : 128) : (isMobile ? 48 : 72)} y={isMobile ? 3 : 3.5} fontSize={isMobile ? 7 : 9} fill="#6b7280" fontWeight={500} fontFamily="Inter, system-ui, sans-serif">Destination</text>
          </g>

          {/* Transport mode icon bottom-right */}
          <text
            x={W - (isMobile ? 10 : 14)}
            y={H - (isMobile ? 8 : 10)}
            textAnchor="end"
            fontSize={isMobile ? 10 : 12}
          >
            {transportMode === 'sea' ? '\u{1F30A}\u{1F6A2}' : '\u2601\uFE0F\u2708\uFE0F'}
          </text>
        </svg>

        {/* Stopped border overlay */}
        {isStopped && (
          <div className="absolute inset-0 rounded-xl border-2 border-red-400 animate-pulse pointer-events-none" style={{ zIndex: 3 }}></div>
        )}
      </div>
    </div>
  );
}
